/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Component, useState } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import set from "lodash/set";
import { AccessType } from "@quintype/components";
import { connect } from "react-redux";
import loadingIcon from "../../../assets/icons/loading.svg";
import { SvgHandler } from "../svg-handler/svg-handler";
import { getQueryParam, isEmpty } from "../../../isomorphic/utils";
import LoaderOverlay from "../molecules/loader-overlay";
import { CaptureUserData } from "../molecules/capture-user-data/index";
import { MEMBER_METADATA_UPDATED, SHOW_MEMBER_METADATA_POPUP } from "../../constants";
import AdyenModal from "../adyen-modal";
import { checkIfSingleSignOn, navigateToAuthDomain } from "../sso-components/sso-utils";

import "./subscription-page.m.css";

export const getSubscriptionGroupsByType = (groups, type) => {
  return groups.filter((subscription) => subscription.metadata_fields.includes(type));
};

export class SubscriptionTemplate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSubscriptions: {},
      isSubscribeButtonClicked: false,
      selectedPlan: {},
    };
  }

  async handleSubscriptionClick(e, subscriptionGroup) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isSubscribeButtonClicked: true });
    this.props.showMemberMetadataPopup(true);

    const firstPlan = get(subscriptionGroup, ["subscription_plans", 0]);
    const selectedPlan = get(this.state, ["selectedSubscriptions", subscriptionGroup.id], firstPlan);
    this.setState({ selectedPlan });

    if (!this.props.member) {
      this.setState({ isWaitingForUserToLogin: true });
      this.props.openLoginTemplate();
    }

    if (!this.props.captureShippingAddress) {
      this.props.subscriberMetadata(true);
    }
  }

  getSupportedRecurringPaymentProvider(plan) {
    const paymentGateways = get(plan, ["supported_payment_providers"], []);
    const supportedGateways = [];
    const planMetadata = get(plan, ["metadata"], {});
    const currency = get(plan, "price_currency");


    paymentGateways.forEach((pg) => {
      // adyen and paytrail are auto linked and they won't appear in metadata
      if (pg === "paytrail" || pg === "adyen") {
        supportedGateways.push(pg);
        return;
      }
      const isLinkedPaymentGateway = get(planMetadata, [pg, "plan_id", currency], false);
      if (isLinkedPaymentGateway) {
        supportedGateways.push(pg);
      }
    });

    return supportedGateways[0]; // TODO: return whole array when we support multiple pgs
  }

  openPaymentScreen(plan = {}) {
    let paymentGateway;

    if (plan.recurring) {
      paymentGateway = this.getSupportedRecurringPaymentProvider(plan);
    } else {
      paymentGateway = get(plan, ["supported_payment_providers", 0], "razorpay");
    }

    if (!paymentGateway) {
      console.error("No payment gateway available to initiate payment");
      return null;
    }

    if (this.props.captureShippingAddress) {
      const userMetaData = this.props.userMetaData || {};
      const { line1, line2, city, country, pincode, state } = userMetaData.address;
      const { dialCode, email } = userMetaData.other;
      const formattedMetadata = {
        ...plan.metadata, 
        phone_number: userMetaData["phone_number"],
        name: `${line1}`,
        address: `${line2}`,
        city: `${city}`,
        country: `${country}`,
        pin_code: `${pincode}`,
        state: `${state}`,
        dial_code: `${dialCode}`,
        email: `${email}`,
      };
      set(plan, ["metadata"], formattedMetadata);
    }

    if (paymentGateway === "razorpay") {
      return this.props.initRazorPayPayment(plan, "standard");
    }
    if (paymentGateway === "paypal") {
      const options = {
        selectedPlan: plan,
        planType: "standard",
        returnUrl: `${document.location.origin}/paypal-payment-success`,
        cancelUrl: `${document.location.origin}/paypal-payment-failed`,
      };
      return this.props.initPaypalPayment(options);
    }
    if (paymentGateway === "omise") {
      return this.props.initOmisePayment(plan, "standard");
    }

    if (paymentGateway === "paytrail") {
      const options = {
        selectedPlan: plan,
        planType: "standard",
        returnUrl: `${document.location.origin}/paytrail-success.html`,
        cancelUrl: `${document.location.origin}/paytrail-cancel.html`,
      };

      return this.props.initPaytrailPayment(options);
    }

    if (paymentGateway === "adyen") {
      const locale = get(
        this.props,
        ["config", "pagebuilder-config", "general", "accesstypeIntegration", "locale"],
        "en-US"
      );
      return this.props.initAdyenPayment(plan, "standard", AdyenModal, locale);
    }

    console.error(`Payment gateway '${paymentGateway}' is not supported yet`);
  }

  navigateToUserDetailsPage() {
    this.props.navigateTo(`/user-details`);
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.member !== this.props.member) {
      this.setState({ isWaitingForUserToLogin: false });
    }
    if (
      this.props.member &&
      this.state.isSubscribeButtonClicked &&
      this.state.selectedPlan &&
      this.state.isAccessTypeUserSet &&
      !this.state.isWaitingForPayment &&
      !this.state.isWaitingForUserToLogin &&
      this.props.isMemberMetadataUpdated
    ) {
      this.setState({
        isWaitingForPayment: true,
      });

      try {
        const subscription = await this.openPaymentScreen(this.state.selectedPlan);
        subscription && this.navigateToUserDetailsPage();
      } finally {
        this.setState({
          isWaitingForPayment: false,
          isSubscribeButtonClicked: false,
          selectedPlan: {},
        });
      }
    }
    if (
      this.props.member &&
      !this.state.isAccessTypeUserSet &&
      !this.state.isAccessTypeUserSetLoading &&
      this.props.isATGlobalAvailable
    ) {
      this.setState({
        isAccessTypeUserSetLoading: true,
      });
      // Bad implementation. Currently AccessType component not exposing a helper
      // fn to call setUser with the jwtToken.
      // Added issue to fix these issues: https://github.com/quintype/quintype-node-components/issues/145
      this.props.initAccessType(() => {
        this.setState({
          isAccessTypeUserSet: true,
          isAccessTypeUserSetLoading: false,
        });
      });
    }
  }

  getSubscriptionBlocks(subscriptionGroups, localizedSubscribe) {
    return subscriptionGroups.map((subscriptionGroup, index) => (
      <div styleName="group" key={`group-${index}`}>
        <div styleName="desc-wrapper">
          <span styleName="subscription-group">{subscriptionGroup.name}</span>
          <div styleName="wrapper-content">
            <p styleName="plan-time">
              {this.getCurrencyType(subscriptionGroups, index)}
              &nbsp;
              {this.getPriceBlock(subscriptionGroups, index)}
              <span styleName="month">{this.getPlanDuration(subscriptionGroups, index)}</span>
            </p>
            {subscriptionGroup?.description && <div styleName="plan-desc" dangerouslySetInnerHTML={{ __html: subscriptionGroup.description }} />}
            {this.getDurationBlock(subscriptionGroup, localizedSubscribe)}
          </div>
        </div>
      </div>
    ));
  }


  getPriceBlock(subscriptionGroups, currentGroupIndex) {
    const firstPlanPrice = get(subscriptionGroups, [currentGroupIndex, "subscription_plans", 0, "price_cents"]);
    return (
      get(
        this.state,
        ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "price_cents"],
        firstPlanPrice
      ) / 100
    );
  }

  getCurrencyType(subscriptionGroups, currentGroupIndex) {
    const firstPlanCurrencyType = get(subscriptionGroups, [
      currentGroupIndex,
      "subscription_plans",
      0,
      "price_currency",
    ]);
    return get(
      this.state,
      ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "price_currency"],
      firstPlanCurrencyType
    );
  }

  getPlanDuration(subscriptionGroups, currentGroupIndex) {
    const firstPlanUnit = get(subscriptionGroups, [currentGroupIndex, "subscription_plans", 0, "duration_unit"]);
    const firstPlanDuration = get(subscriptionGroups, [currentGroupIndex, "subscription_plans", 0, "duration_length"]);
    const planUnit = get(
      this.state,
      ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "duration_unit"],
      firstPlanUnit
    );
    const planDuration = get(
      this.state,
      ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "duration_length"],
      firstPlanDuration
    );
    let updatedPlanUnit = planUnit;

    if (!isEmpty(this.props.localizedTime)) {
      const hasKey = planUnit in this.props.localizedTime;
      updatedPlanUnit = hasKey ? this.props.localizedTime[planUnit] : planUnit;
    }

    return (
      <span>
        <span>{planDuration}</span>
        <span>{updatedPlanUnit}</span>
      </span>
    );
  }

  getDurationBlock(subscriptionGroup, localizedSubscribe) {
    const plans = get(subscriptionGroup, ["subscription_plans"], []);
    const subscriptionGroupId = get(subscriptionGroup, ["id"]);
    const sso = get(this.props, ["config", "pagebuilder-config", "general", "sso"], {});
    const { bridgeKeeperClientId = "" } = sso;

    const planList = (
      <div styleName="select-wrapper">
        <select onChange={(e) => this.handlePlanChange(e, subscriptionGroupId)} styleName="plan-selection">
          {plans.map((plan, index) => (
            <option key={`plan-${index}`} value={JSON.stringify(plan)}>
              {plan.title}
            </option>
          ))}
        </select>
      </div>
    );
    return (
      <div styleName="duration-wrapper">
        {planList}

        <button
          data-testid="plans-subscribe-btn"
          styleName="subs-btn"
          onClick={(e) =>
            (!this.props.member || !this.props.member["verification-status"]) && checkIfSingleSignOn(sso)
              ? navigateToAuthDomain(bridgeKeeperClientId)
              : this.handleSubscriptionClick(e, subscriptionGroup)
          }
        >
          {localizedSubscribe || "Subscribe"}
        </button>
      </div>
    );
  }

  handlePlanChange(e, subscriptionGroupId) {
    const currentPlan = JSON.parse(e.target.value);
    const currentState = Object.assign({}, this.state.selectedSubscriptions, {
      [subscriptionGroupId]: currentPlan,
    });
    this.setState({ selectedSubscriptions: currentState });
  }

  render() {
    const PbAccessTypeConfig = get(
      this.props,
      ["config", "pagebuilder-config", "general", "accesstypeIntegration"],
      {}
    );
    let planLayout = get(PbAccessTypeConfig, ["planPageLayout"], "");
    const planTitle = get(PbAccessTypeConfig, ["planPageTitle"], "");
    const planDesc = get(PbAccessTypeConfig, ["planPageDescription"], "");

    const groupType = getQueryParam(this.props.currentPath, "group");
    const subscriptions = get(this.props, ["subscriptions"]) || [];
    const groups =
      (groupType && subscriptions.length && getSubscriptionGroupsByType(subscriptions, groupType)) || subscriptions;
    planLayout = groups.length <= 2 ? "list-view" : planLayout;

    return (
      <>
        {this.state.isAccessTypeUserSetLoading ? <LoaderOverlay /> : null}
        <div className="component-wrapper" styleName={`${planLayout === "list-view" ? "component-wrapper" : " "}`}>
          <div styleName="content-wrapper">
            <h1 styleName="page-title">{planTitle}</h1>
            <p>{planDesc}</p>
          </div>
          {groups && groups.length > 0 ? (
            <div styleName={`${planLayout === "list-view" ? "list-view" : "groups-layout"}`}>
              {this.getSubscriptionBlocks(groups, this.props.localizedSubscribe)}
            </div>
          ) : (
            <SvgHandler clazzName="loader" xlinkHref={loadingIcon} />
          )}
        </div>
        {this.props.member && this.state.isSubscribeButtonClicked && this.props.captureShippingAddress && (
          <CaptureUserData
            member={this.props.member}
            getSubscriberMetadata={this.props.getSubscriberMetadata}
            setSubscriberMetadata={this.props.setSubscriberMetadata}
          />
        )}
      </>
    );
  }
}

SubscriptionTemplate.propTypes = {
  initAccessType: PropTypes.func,
  initRazorPayPayment: PropTypes.func,
  initOmisePayment: PropTypes.func,
  initPaypalPayment: PropTypes.func,
  initAdyenPayment: PropTypes.func,
  openLoginTemplate: PropTypes.func,
  subscriberMetadata: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  navigateTo: PropTypes.func,
  member: PropTypes.object,
  getSubscription: PropTypes.func,
  currentPath: PropTypes.string,
  localizedSubscribe: PropTypes.string,
  captureShippingAddress: PropTypes.bool,
  isMemberMetadataUpdated: PropTypes.bool,
  getSubscriberMetadata: PropTypes.func,
  setSubscriberMetadata: PropTypes.func,
  localizedTime: PropTypes.shape({
    weeks: PropTypes.string,
    months: PropTypes.string,
    years: PropTypes.string,
    days: PropTypes.string,
  }),
};

function SubscriptionBase(props) {
  const email = get(props, ["member", "email"]);
  const phone = get(props, ["member", "metadata", "phone-number"], "1234");
  const PbAccessTypeConfig = get(props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"], {});
  const [isATGlobalAvailable, setATGlobal] = useState(false);
  const isStaging = get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging";
  const {
    accesstypeKey = "",
    bridgeKeeperIntegrationId = "",
    enableAccesstype = false,
    captureShippingAddress = false,
    accessTypeProdHost = "",
    accessTypeStgHost = "",
  } = PbAccessTypeConfig;

  const storyId = get(props, ["story", "id"]);

  if (!enableAccesstype) return null;

  return (
    <AccessType
      enableAccesstype={enableAccesstype}
      isStaging={isStaging}
      accessTypeKey={accesstypeKey}
      accessTypeBkIntegrationId={bridgeKeeperIntegrationId}
      email={email}
      phone={phone}
      storyId={storyId}
      prodHost={accessTypeProdHost}
      stagingHost={accessTypeStgHost}
      onATGlobalSet={() => {
        setATGlobal(true);
      }}
    >
      {({
        initAccessType,
        initRazorPayPayment,
        initPaypalPayment,
        initOmisePayment,
        initAdyenPayment,
        initPaytrailPayment,
        getSubscriberMetadata,
        setSubscriberMetadata,
      }) => (
        <SubscriptionTemplate
          initPaypalPayment={initPaypalPayment}
          initOmisePayment={initOmisePayment}
          initRazorPayPayment={initRazorPayPayment}
          initAccessType={initAccessType}
          initAdyenPayment={initAdyenPayment}
          initPaytrailPayment={initPaytrailPayment}
          setSubscriberMetadata={setSubscriberMetadata}
          getSubscriberMetadata={getSubscriberMetadata}
          captureShippingAddress={captureShippingAddress}
          isATGlobalAvailable={isATGlobalAvailable}
          {...props}
        />
      )}
    </AccessType>
  );
}

const mapStateToProps = (state) => {
  const { enableLocalization = false, localizedElements = {} } = get(
    state,
    ["qt", "config", "pagebuilder-config", "general", "localization"],
    {}
  );
  return {
    config: get(state, ["qt", "config"], {}),
    subscriptions: get(state, ["subscriptions"], null),
    member: get(state, ["member"], null),
    localizedSubscribe: enableLocalization && get(localizedElements, ["buttonLabels", "subscribe"]),
    isMemberMetadataUpdated: get(state, ["memberMetadata", "isMemberMetadataUpdated"], false),
    userMetaData: get(state, ["memberMetadata", "data"], {}),
    localizedTime: enableLocalization ? get(localizedElements, ["publishTime"], {}) : {},
  };
};

const mapDispatchToProps = (dispatch) => ({
  openLoginTemplate: () => dispatch({ type: "OPEN_LOGIN_TEMPLATE" }),
  navigateTo: (url) => global.app.navigateToPage(dispatch, url),
  subscriberMetadata: (isMemberMetadataUpdated) => dispatch({ type: MEMBER_METADATA_UPDATED, isMemberMetadataUpdated }),
  showMemberMetadataPopup: (showMemberMetadataPopup) =>
    dispatch({ type: SHOW_MEMBER_METADATA_POPUP, showMemberMetadataPopup }),
});

export const Subscription = connect(mapStateToProps, mapDispatchToProps)(SubscriptionBase);
